import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { SkeletonText, InlineNotification } from 'carbon-components-react';
import {
  getWebLaunchRequestStart,
  clearWebLaunchRequest
} from 'redux/webLauncherLanding/actions';
import { setShowInterstitialPage } from 'redux/app/actions';
import { prePopulateEnquiryData } from 'redux/lodgeEnquiry/actions';
import {
  selectWebLaunchRequestError,
  selectWebLaunchRequest
} from 'redux/webLauncherLanding/selectors';
import { useParams, useHistory } from 'react-router';
import { useComponentMount } from 'utilities/hooks.util';
import { WebLaunchRequest, APP_URL } from 'types';

interface ParamTypes {
  webLaunchRequestId: string;
  pageType?: string;
}

const WebLauncherLanding: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { webLaunchRequestId, pageType } = useParams<ParamTypes>();
  const webLaunchRequest = useSelector(selectWebLaunchRequest);
  const webLaunchRequestError = useSelector(selectWebLaunchRequestError);
  const [errorMessage, setErrorMessage] = useState('');

  const handleWebAutomationRequest = useCallback(
    (request: WebLaunchRequest) => {
      if (pageType === 'enquirydetail') {
        if (!request.response?.enquiryId) {
          return setErrorMessage('Enquiry not found');
        }
        return history.push(
          `${APP_URL.ENQUIRY_DETAILS}`.replace(
            ':id',
            request.response.enquiryId
          )
        );
      }

      if (pageType === 'enquirycreate') {
        if (request.response?.enquiryId) {
          return setErrorMessage('Enquiry created');
        }
        if (!request.payload?.shape) {
          return setErrorMessage(`Enquiry's geometry missing`);
        }
        dispatch(setShowInterstitialPage(false));
        dispatch(
          prePopulateEnquiryData({
            ...request.payload,
            webLaunchRequestId: request.uuid
          })
        );
        history.push({
          pathname: APP_URL.LODGE_NEW_ENQUIRY,
          state: {
            from: location?.pathname
          }
        });
      }
    },
    [pageType, history, location, dispatch]
  );

  useEffect(() => {
    dispatch(getWebLaunchRequestStart(webLaunchRequestId));
  }, [webLaunchRequestId, dispatch]);

  useEffect(() => {
    if (webLaunchRequest) {
      switch (webLaunchRequest.requestType) {
        case 'WEB_ENQUIRY_AUTOMATION':
          handleWebAutomationRequest(webLaunchRequest);
          break;
        default:
          setErrorMessage('Unknown request');
      }
    }
  }, [webLaunchRequest, handleWebAutomationRequest]);

  useEffect(() => {
    webLaunchRequestError && setErrorMessage(webLaunchRequestError);
  }, [webLaunchRequestError]);

  useComponentMount(() => {
    return () => {
      dispatch(clearWebLaunchRequest());
    };
  });

  return (
    <div id="WebLauncherLanding" className="web-launcher-landing">
      {errorMessage ? (
        <InlineNotification
          kind="error"
          hideCloseButton={true}
          title={errorMessage}
        />
      ) : (
        <SkeletonText heading={true} />
      )}
    </div>
  );
};

export default WebLauncherLanding;
